import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  getCompanyProfile,
  saveCompanyProfile,
  type CompanyProfile,
  type CompanyProfileInput,
} from '../lib/companyProfileApi';
import { readBrandCache, writeBrandCache, type CachedBrand } from '../lib/brandCache';

// React Query hooks for the company profile (SaaS hardening).

const COMPANY_PROFILE_KEY = ['company-profile'] as const;

/** Neutral product default, used only when no company profile exists at all. */
export const DEFAULT_BRAND_NAME = 'Firmio';

function brandOf(profile: CompanyProfile | null): CachedBrand {
  return {
    name: profile?.displayName?.trim() || profile?.legalName?.trim() || DEFAULT_BRAND_NAME,
    logoUrl: profile?.logoUrl ?? null,
  };
}

export function useCompanyProfile() {
  return useQuery({
    queryKey: COMPANY_PROFILE_KEY,
    // Mirror the brand into localStorage on every successful fetch so the next
    // page load can paint the correct name/logo immediately (see brandCache).
    queryFn: async () => {
      const profile = await getCompanyProfile();
      writeBrandCache(brandOf(profile));
      return profile;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface Brand extends CachedBrand {
  /**
   * False only while the very first load of a session is still resolving with
   * nothing cached. Callers render a neutral placeholder instead of guessing a
   * name, so the chrome never shows one brand and then swaps to another.
   */
  isReady: boolean;
}

/**
 * Brand shown in the app chrome: the configured company display name (falling
 * back to the legal name) plus its square logo.
 */
export function useBrand(): Brand {
  const { data } = useCompanyProfile();
  // Read once per mount: the cache exists to cover the first paint, and
  // re-reading it on every render would compete with the fresh server value.
  const [cached] = useState(readBrandCache);

  if (data !== undefined) return { ...brandOf(data), isReady: true };
  if (cached) return { ...cached, isReady: true };
  return { name: DEFAULT_BRAND_NAME, logoUrl: null, isReady: false };
}

export function useSaveCompanyProfile() {
  const qc = useQueryClient();
  return useMutation<CompanyProfile, Error, CompanyProfileInput>({
    mutationFn: (input) => saveCompanyProfile(input),
    onSuccess: (profile) => {
      qc.setQueryData(COMPANY_PROFILE_KEY, profile);
      writeBrandCache(brandOf(profile));
    },
  });
}
